import { Link as RouterLink } from 'react-router';

import { getJudgeFactoryPath } from '../../../features/judge-factory';
import type { Grader } from '../../../hooks/useGraders/useGraders.interface.js';
import { graderTypeLabel } from '../../../services/grader.interface.js';
import { useStyles } from '../GraderPage.style.js';
import { renderGraderOverviewNotice } from '../graderTypes/graderTypeRegistry.js';

const AUGLOOP_SOURCE_PREFIX = '/utils/teams-quality-evaluator/src/evals/';

function sourceLink(path: string | undefined): string | undefined {
  if (
    !path?.startsWith(AUGLOOP_SOURCE_PREFIX) ||
    path.split('/').includes('..') ||
    /[?#\\]/.test(path)
  ) {
    return undefined;
  }
  const url = new URL(
    'https://dev.azure.com/office/ISS/_git/augloop-workflows',
  );
  url.searchParams.set('path', path);
  url.searchParams.set('version', 'GBmain');
  return url.toString();
}

function Detail({
  label,
  value,
}: {
  label: string;
  value: string | undefined;
}) {
  const styles = useStyles();
  return (
    <div>
      <dt className={styles.readOnlyLabel}>{label}</dt>
      <dd className={styles.readOnlyValue}>{value?.trim() ? value : '—'}</dd>
    </div>
  );
}

function Prompt({ title, text }: { title: string; text: string | undefined }) {
  const styles = useStyles();
  return (
    <section className={styles.overviewCard} aria-label={title}>
      <div className={styles.overviewCardTitle}>{title}</div>
      <pre className={styles.readOnlyPrompt}>
        {text?.trim() ? text : 'No prompt provided.'}
      </pre>
    </section>
  );
}

export function GraderReadOnlyOverview({
  judge,
  workspaceName,
}: {
  judge: Grader;
  workspaceName: string;
}) {
  const styles = useStyles();
  const variables = Object.entries(judge.variables ?? {});
  const isLlm = judge.graderType === 'LlmGraderSpec';
  const isNumeric = judge.graderType === 'NumericComparisonGraderSpec';
  const isLatency = judge.graderType === 'LatencyGraderSpec';
  const workbenchUrl = sourceLink(judge.sourcePath);

  return (
    <div className={styles.overviewTab}>
      {renderGraderOverviewNotice(judge)}
      <section className={styles.overviewCard} aria-label="Judge details">
        <h2 className={styles.readOnlyTitle}>
          {judge.displayName?.trim() ? judge.displayName : judge.name}
        </h2>
        <p className={styles.readOnlyDescription}>
          {judge.description?.trim()
            ? judge.description
            : 'No description provided.'}
        </p>
        <dl className={styles.readOnlyDetails}>
          <Detail label="Name" value={judge.name} />
          <Detail label="Type" value={graderTypeLabel(judge.graderType)} />
          {isLlm && <Detail label="Model" value={judge.model} />}
        </dl>
      </section>

      {(Boolean(judge.sourceGraderGenId) || Boolean(workbenchUrl)) && (
        <section className={styles.overviewCard} aria-label="Source">
          <div className={styles.overviewCardTitle}>Source</div>
          {workbenchUrl && (
            <div>
              <a
                className={styles.detailLink}
                href={workbenchUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Workbench definition
              </a>
              <div className={styles.readOnlySourcePath}>
                {judge.sourcePath}
              </div>
            </div>
          )}
          {judge.sourceGraderGenId && (
            <p className={styles.overviewCardBody}>
              Created in the Judge Factory from judge{' '}
              <RouterLink
                className={styles.detailLink}
                to={getJudgeFactoryPath(workspaceName, judge.sourceGraderGenId)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {judge.sourceGraderGenName ?? judge.sourceGraderGenId}
              </RouterLink>
              .
            </p>
          )}
        </section>
      )}

      {isLlm && (
        <>
          <Prompt title="System prompt" text={judge.systemPrompt} />
          {judge.userTemplate && (
            <section className={styles.overviewCard} aria-label="Invocation">
              <div className={styles.overviewCardTitle}>User message</div>
              <pre className={styles.readOnlyText}>{judge.userTemplate}</pre>
            </section>
          )}
          {(Boolean(judge.temperature) ||
            Boolean(judge.topP) ||
            Boolean(judge.maxTokens)) && (
            <section
              className={styles.overviewCard}
              aria-label="Model settings"
            >
              <div className={styles.overviewCardTitle}>Model settings</div>
              <dl className={styles.readOnlyDetails}>
                {judge.temperature && (
                  <Detail label="Temperature" value={judge.temperature} />
                )}
                {judge.topP && <Detail label="Top P" value={judge.topP} />}
                {judge.maxTokens && (
                  <Detail label="Max tokens" value={judge.maxTokens} />
                )}
              </dl>
            </section>
          )}
        </>
      )}

      {isNumeric && (
        <section className={styles.overviewCard} aria-label="Comparison">
          <div className={styles.overviewCardTitle}>Comparison</div>
          <dl className={styles.readOnlyDetails}>
            <Detail label="Min scale" value={judge.numeric?.minScale} />
            <Detail label="Max scale" value={judge.numeric?.maxScale} />
            <Detail label="Tolerance" value={judge.numeric?.tolerance} />
            <Detail label="Step" value={judge.numeric?.step} />
            <Detail
              label="Expected score"
              value={judge.numeric?.scoreExpected}
            />
            <Detail label="Actual score" value={judge.numeric?.scoreActual} />
          </dl>
        </section>
      )}

      {isLatency && (
        <section className={styles.overviewCard} aria-label="Latency">
          <div className={styles.overviewCardTitle}>Latency</div>
          <dl className={styles.readOnlyDetails}>
            <Detail label="Metric name" value={judge.latency?.metricName} />
            <Detail
              label="Latency (ms) expression"
              value={judge.latency?.latencyMs}
            />
          </dl>
        </section>
      )}

      {!isLlm && !isNumeric && !isLatency && judge.rawSpec && (
        <section className={styles.overviewCard} aria-label="Configuration">
          <div className={styles.overviewCardTitle}>Configuration</div>
          <pre className={styles.readOnlyText}>
            {JSON.stringify(judge.rawSpec, null, 2)}
          </pre>
        </section>
      )}

      {variables.length > 0 && (
        <section className={styles.overviewCard} aria-label="Variables">
          <div className={styles.overviewCardTitle}>Variables</div>
          <dl className={styles.readOnlyDetails}>
            {variables.map(([name, variable]) => (
              <Detail
                key={name}
                label={name}
                value={`${variable.type}${variable.description ? ` — ${variable.description}` : ''}`}
              />
            ))}
          </dl>
        </section>
      )}
    </div>
  );
}
